// =============================================================================
// MeuDriver — Admin: revisar documento de identidade do instrutor
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Eye, Check, X, IdCard, Loader2 } from 'lucide-react'

type DocStatus = 'not_submitted' | 'pending' | 'verified' | 'rejected' | null

interface Props {
  instructorId: string
  instructorProfileId: string
  docStatus?: DocStatus
  onUpdated?: () => void
}

const STATUS_META: Record<Exclude<DocStatus, null | 'not_submitted'>, { label: string; cls: string }> = {
  pending:  { label: 'Pendente',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  verified: { label: 'Verificado',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejeitado',   cls: 'bg-red-100 text-red-800 border-red-200' },
}

export function AdminIdentityDocReviewButton({
  instructorId,
  instructorProfileId,
  docStatus,
  onUpdated,
}: Props) {
  const [open, setOpen] = useState(false)
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function load() {
    setLoading(true)
    try {
      // Recupera o path do documento no banco
      const { data: inst } = await supabase
        .from('instructors')
        .select('identity_doc_url')
        .eq('id', instructorId)
        .maybeSingle()

      const docPath = (inst as { identity_doc_url?: string | null } | null)?.identity_doc_url

      if (!docPath) {
        setSignedUrl(null)
        return
      }

      const { data: signed } = await supabase.storage
        .from('identity-docs')
        .createSignedUrl(docPath, 60 * 10) // 10 min

      setSignedUrl(signed?.signedUrl ?? null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function decide(action: 'verified' | 'rejected') {
    setSubmitting(true)
    const { error } = await supabase
      .from('instructors')
      .update({
        identity_doc_status: action,
        identity_doc_note: note.trim() || null,
        identity_doc_reviewed_at: new Date().toISOString(),
      })
      .eq('id', instructorId)
    setSubmitting(false)

    if (error) {
      toast.error(error.message)
      return
    }

    toast.success(action === 'verified' ? 'Documento verificado.' : 'Documento rejeitado.')
    setOpen(false)
    setNote('')
    onUpdated?.()
  }

  const isImage = signedUrl ? /\.(jpg|jpeg|png|webp)(\?|$)/i.test(signedUrl) : false
  const hasReviewableStatus = docStatus && docStatus !== 'not_submitted'

  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-[11px] gap-1"
          onClick={() => setOpen(true)}
          data-testid="admin-identity-doc-btn"
        >
          <Eye className="h-3 w-3" />
          ID
        </Button>
        {docStatus && docStatus !== 'not_submitted' && (
          <Badge
            variant="outline"
            className={`text-[9px] px-1 ${STATUS_META[docStatus as Exclude<DocStatus, null | 'not_submitted'>].cls}`}
          >
            {STATUS_META[docStatus as Exclude<DocStatus, null | 'not_submitted'>].label}
          </Badge>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IdCard className="h-5 w-5 text-brand-600" />
              Verificação de documento de identidade
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !signedUrl ? (
              <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
                {hasReviewableStatus
                  ? 'Não foi possível carregar o documento.'
                  : 'Este instrutor ainda não enviou documento de identidade.'}
              </div>
            ) : isImage ? (
              <img
                src={signedUrl}
                alt="Documento de identidade"
                className="rounded-md border max-h-96 mx-auto"
              />
            ) : (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-600 hover:underline"
              >
                Abrir documento (PDF) em nova aba
              </a>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Observação (visível ao instrutor se rejeitar)
              </label>
              <Textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ex: foto cortada, use um documento com foto legível."
                data-testid="identity-doc-note"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Fechar
            </Button>
            <Button
              variant="outline"
              onClick={() => decide('rejected')}
              disabled={submitting || !signedUrl}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="identity-doc-reject-btn"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Rejeitar
            </Button>
            <Button
              onClick={() => decide('verified')}
              disabled={submitting || !signedUrl}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="identity-doc-approve-btn"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
